import React, { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";

/**
 * Legacy URL redirect — handles URLs from the previous WordPress-era site
 * that Google Search Console still knows about but that no longer exist.
 *
 * Strategy for SEO recovery:
 * 1. `<meta http-equiv="refresh" content="0; url=NEW_URL">` — instructs crawlers
 *    and browsers to follow. Works even when JS is disabled.
 * 2. `<link rel="canonical" href="NEW_URL">` — tells Google to consolidate signals
 *    into the destination URL.
 * 3. `<Navigate replace>` — real-time client redirect for the ~99% of users
 *    who have JS.
 * 4. Fallback `<a>` link — for JS-off scenarios so the user isn't stranded.
 *
 * All these are prerendered by react-snap so the HTML delivered by the host
 * already contains the refresh + canonical tags — no JS execution needed for
 * search crawlers to follow.
 */

// Map: legacy path (WordPress-style, may include trailing slash) → new destination.
export const LEGACY_MAP = {
  "/o-que-esperar-das-aulas-de-frances-dinamicas": "/blog/aula-de-frances-gratis",
  "/descubra-o-seu-potencial-no-frances-hoje": "/teste-de-nivel",
  "/frances-um-idioma-para-novas-oportunidades": "/blog/10-erros-brasileiros-aprendendo-frances",
};

export const LEGACY_PATHS = Object.keys(LEGACY_MAP);

const LegacyRedirect = ({ to }) => {
  const target = to || "/";
  const fullTarget = `https://npdlbr.com.br${target}`;

  useEffect(() => {
    // Inject meta refresh + canonical into <head> (survives prerender)
    const refresh = document.createElement("meta");
    refresh.setAttribute("http-equiv", "refresh");
    refresh.setAttribute("content", `0; url=${target}`);
    refresh.dataset.dynamic = "legacy-redirect";
    document.head.appendChild(refresh);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const prevHref = canonical?.getAttribute("href") || null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      canonical.dataset.dynamic = "legacy-redirect";
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", fullTarget);

    return () => {
      refresh.remove();
      if (canonical && canonical.dataset.dynamic === "legacy-redirect") {
        canonical.remove();
      } else if (canonical && prevHref !== null) {
        canonical.setAttribute("href", prevHref);
      }
    };
  }, [target, fullTarget]);

  return (
    <>
      <Navigate to={target} replace />
      {/* Visible for JS-off crawlers and paranoid users */}
      <div
        data-testid="legacy-redirect-notice"
        style={{
          padding: "60px 24px",
          maxWidth: 640,
          margin: "0 auto",
          fontFamily: "system-ui, sans-serif",
          textAlign: "center",
          color: "#1e2a5a",
        }}
      >
        <p style={{ fontSize: 18, marginBottom: 16 }}>
          Esta página foi movida. Redirecionando…
        </p>
        <Link
          to={target}
          style={{
            display: "inline-block",
            padding: "12px 24px",
            borderRadius: 999,
            background: "#d9381e",
            color: "#f4f1ea",
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          Ir agora para o conteúdo &rarr;
        </Link>
      </div>
    </>
  );
};

export default LegacyRedirect;
